import React, { useEffect, useState } from "react";
import { Minus, Plus, Save, Loader2, Check, ShoppingBasket } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError } from "@/lib/api";

export default function QuickPurchase({ date }) {
  const [suppliers, setSuppliers] = useState([]);
  const [sid, setSid] = useState("");
  const [products, setProducts] = useState([]);
  const [existing, setExisting] = useState(null);
  const [qty, setQty] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/suppliers").then((r) => { setSuppliers(r.data); if (!sid && r.data[0]) setSid(r.data[0].id); }); /* eslint-disable-next-line */ }, []);

  const load = async () => {
    if (!sid) return;
    const [p, e] = await Promise.all([
      api.get("/supplier-products", { params: { supplier_id: sid } }),
      api.get("/supplier-purchases/daily", { params: { date, supplier_id: sid } }),
    ]);
    setProducts(p.data);
    const ex = e.data[0] || null;
    setExisting(ex);
    setQty(ex ? Object.fromEntries(ex.items.map((i) => [i.product_id, i.qty])) : {});
    setDirty(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sid, date]);

  const setQ = (id, v) => { setQty((q) => ({ ...q, [id]: Math.max(0, parseFloat(v) || 0) })); setDirty(true); };
  const total = products.reduce((s, p) => s + (qty[p.id] || 0) * p.price, 0);
  const count = products.reduce((s, p) => s + (qty[p.id] || 0), 0);

  const save = async () => {
    const items = products.filter((p) => qty[p.id] > 0).map((p) => ({ product_id: p.id, qty: qty[p.id] }));
    if (!items.length) { toast.error("En az bir ürün için adet girin"); return; }
    setSaving(true);
    try {
      if (existing) await api.put(`/supplier-purchases/daily/${existing.id}`, { supplier_id: sid, date, items });
      else await api.post("/supplier-purchases/daily", { supplier_id: sid, date, items });
      toast.success(`Alım kaydedildi · ${TRY(total)} maliyete işlendi`); load();
    } catch (e) { toast.error(e.response ? formatApiError(e.response?.data?.detail) : "Çevrimdışı — alım girişi için bağlantı gerekli"); }
    finally { setSaving(false); }
  };

  if (suppliers.length === 0) return <div className="text-center text-sm text-muted-foreground py-8" data-testid="quick-purchase-no-suppliers">Henüz tedarikçi yok. Tedarikçiler sekmesinden ekleyin.</div>;

  return (
    <div className="space-y-3" data-testid="quick-purchase">
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="quick-purchase-suppliers">
        {suppliers.map((s) => (
          <button key={s.id} onClick={() => setSid(s.id)} data-testid={`quick-supplier-${s.id}`}
            className={`h-10 px-4 rounded-full border text-sm font-semibold whitespace-nowrap transition-colors ${sid === s.id ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:bg-accent"}`}>{s.name}</button>
        ))}
      </div>
      {existing && !dirty && <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-400 flex items-center gap-2" data-testid="quick-purchase-saved"><Check className="h-4 w-4" /> Bu gün için alım kayıtlı · {TRY(existing.amount)} — değiştirip tekrar kaydedebilirsiniz</div>}
      {products.length === 0 && <div className="text-center text-sm text-muted-foreground py-8" data-testid="quick-purchase-no-products">Bu tedarikçi için ürün tanımlı değil. Tedarikçiler → Ürünler & Fiyatlar'dan ekleyin.</div>}
      {products.map((p) => { const q = qty[p.id] || 0; return (
        <div key={p.id} data-testid={`quick-product-row-${p.id}`} className={`rounded-xl border p-3 flex items-center gap-3 ${q > 0 && existing && !dirty ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-card"}`}>
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate text-sm">{p.name}</div>
            <div className="text-[11px] text-muted-foreground font-mono">{TRY(p.price)} × {q} {p.unit} = <span className="text-foreground font-semibold">{TRY(q * p.price)}</span></div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setQ(p.id, q - 10)} data-testid={`quick-product-minus-${p.id}`} className="h-11 w-11 rounded-lg border border-border flex items-center justify-center active:bg-accent"><Minus className="h-4 w-4" /></button>
            <input type="number" inputMode="decimal" value={q} onChange={(e) => setQ(p.id, e.target.value)} onFocus={(e) => e.target.select()}
              data-testid={`quick-product-input-${p.id}`} className="h-11 w-16 rounded-lg border border-border bg-background text-center font-mono text-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary" />
            <button onClick={() => setQ(p.id, q + 10)} data-testid={`quick-product-plus-${p.id}`} className="h-11 w-11 rounded-lg border border-border flex items-center justify-center active:bg-accent"><Plus className="h-4 w-4" /></button>
          </div>
        </div>
      ); })}

      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-background/95 backdrop-blur px-4 py-3">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><ShoppingBasket className="h-3 w-3" /> {count} adet · {suppliers.find((s) => s.id === sid)?.name}</div>
            <div className="font-mono text-lg font-bold text-rose-400 truncate" data-testid="quick-purchase-total">{TRY(total)}</div>
          </div>
          <button onClick={save} disabled={saving || !dirty || total <= 0} data-testid="quick-purchase-save"
            className="h-12 px-6 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}
